package com.example.event_management_system.ui.screens.events

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.data.repository.IAuthRepository
import com.example.event_management_system.data.repository.IEmailRepository
import com.example.event_management_system.data.repository.IEventRepository
import com.example.event_management_system.data.repository.ILocationRepository
import com.example.event_management_system.data.repository.IUserRepository
import kotlin.time.Instant
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

data class EventDetailState(
    val event: Event? = null,
    val currentUser: User? = null,
    val isLoading: Boolean = true,
    val error: String? = null,
    val isDeleting: Boolean = false,
    val isDeleted: Boolean = false,
    val showDeleteConfirm: Boolean = false,
    val showPrintConfirm: Boolean = false,
    val showEmailMissing: Boolean = false,
    val isSendingEmail: Boolean = false,
    val emailMessage: String? = null,
    val showEmailPreview: Boolean = false,
    val emailDraftBody: String = "",
    val locationEmailBodyTemplate: String? = null,
    val locationEmailSignatureLine: String? = null,
    val locationAddress: String? = null,
    val locationPhone: String? = null,
    val emailSentSuccess: Boolean = false
)

class EventDetailScreenModel(
    private val eventRepository: IEventRepository,
    private val userRepository: IUserRepository,
    private val authRepository: IAuthRepository,
    private val emailRepository: IEmailRepository,
    private val locationRepository: ILocationRepository
) : ScreenModel {

    private val _state = MutableStateFlow(EventDetailState())
    val state: StateFlow<EventDetailState> = _state.asStateFlow()

    private var eventId: String = ""

    fun loadEvent(eventId: String) {
        this.eventId = eventId
        screenModelScope.launch {
            _state.update { it.copy(isLoading = true) }

            // Load current user
            val userId = authRepository.getCurrentUserId()
            if (userId != null) {
                val user = userRepository.getUser(userId)
                _state.update { it.copy(currentUser = user) }
            }

            // Load event
            val event = eventRepository.getEvent(eventId)
            if (event != null) {
                val location = if (event.locationId.isNotBlank())
                    locationRepository.getLocation(event.locationId) else null
                _state.update {
                    it.copy(
                        event = event,
                        isLoading = false,
                        error = null,
                        locationEmailBodyTemplate = location?.emailBodyTemplate,
                        locationEmailSignatureLine = location?.emailSignatureLine,
                        locationAddress = location?.address,
                        locationPhone = location?.phone
                    )
                }
            } else {
                _state.update { it.copy(isLoading = false, error = "Event not found") }
            }
        }
    }

    fun canDelete(): Boolean {
        val user = _state.value.currentUser ?: return false
        return user.role == UserRole.OWNER || user.role == UserRole.MANAGER
    }

    fun showDeleteConfirmation() {
        _state.update { it.copy(showDeleteConfirm = true) }
    }

    fun hideDeleteConfirmation() {
        _state.update { it.copy(showDeleteConfirm = false) }
    }

    fun showPrintConfirmation() {
        _state.update { it.copy(showPrintConfirm = true) }
    }

    fun hidePrintConfirmation() {
        _state.update { it.copy(showPrintConfirm = false) }
    }

    fun sendToHost() {
        val event = _state.value.event ?: return
        if (event.customerEmail.isBlank()) {
            _state.update { it.copy(showEmailMissing = true) }
            return
        }
        if (_state.value.isSendingEmail) return
        val s = _state.value
        val senderName = s.currentUser?.displayName ?: ""
        val staffPhone = s.currentUser?.phone
        val draft = buildEmailPreview(event, senderName, staffPhone, s.locationEmailBodyTemplate, s.locationEmailSignatureLine)
        _state.update { it.copy(showEmailPreview = true, emailDraftBody = draft) }
    }

    fun dismissEmailPreview() {
        _state.update { it.copy(showEmailPreview = false) }
    }

    fun confirmSendEmail() {
        val event = _state.value.event ?: return
        val body = _state.value.emailDraftBody
        _state.update { it.copy(showEmailPreview = false, isSendingEmail = true, emailMessage = null) }
        screenModelScope.launch {
            val result = emailRepository.sendEventConfirmation(event.id, bodyOverride = body)
            _state.update {
                it.copy(
                    isSendingEmail = false,
                    emailSentSuccess = result.isSuccess,
                    emailMessage = if (result.isSuccess) null
                    else result.exceptionOrNull()?.message ?: "Failed to send confirmation email."
                )
            }
        }
    }

    private fun buildEmailPreview(
        event: Event,
        senderName: String,
        staffPhone: String? = null,
        bodyTemplate: String? = null,
        signatureOverride: String? = null
    ): String {
        val s = _state.value
        val template = bodyTemplate?.takeIf { it.isNotBlank() } ?: DEFAULT_EMAIL_TEMPLATE
        val roleStr = s.currentUser?.role?.name
        val locName = event.locationName.ifBlank { null }
        val signature = if (!signatureOverride.isNullOrBlank())
            applySignatureTemplate(signatureOverride, senderName, roleStr, staffPhone, locName, s.locationAddress, s.locationPhone)
            else buildSignature(senderName, staffPhone, locName)
        return applyTemplate(template, event, signature)
    }

    private fun buildSignature(
        senderName: String,
        staffPhone: String?,
        locationName: String?
    ): String = listOfNotNull(
        senderName.ifBlank { null },
        staffPhone?.ifBlank { null },
        locationName?.ifBlank { null }
    ).joinToString("\n")

    private fun applySignatureTemplate(
        template: String,
        senderName: String,
        userRole: String?,
        staffPhone: String?,
        locationName: String?,
        locationAddress: String?,
        locationPhone: String?
    ): String = template
        .replace("{{staffName}}", senderName)
        .replace("{{staffRole}}", userRole?.lowercase()?.replaceFirstChar { it.uppercase() } ?: "")
        .replace("{{staffPhone}}", staffPhone ?: "")
        .replace("{{locationName}}", locationName ?: "")
        .replace("{{locationAddress}}", locationAddress ?: "")
        .replace("{{locationPhone}}", locationPhone ?: "")
        .replace("{{logo}}", "%%LOGOIMG%%")

    private fun applyTemplate(
        template: String,
        event: Event,
        signature: String
    ): String {
        val locationName = event.locationName.ifBlank { "" }
        val date = formatEmailDate(event.eventDate)
        val timeRange = listOfNotNull(
            event.eventTime.ifBlank { null },
            event.eventEndTime?.ifBlank { null }
        ).joinToString(" - ")
        val notesBlock = if (!event.notes.isNullOrBlank()) "Notes:\n${event.notes}" else ""
        val purposeBlock = if (!event.eventPurpose.isNullOrBlank()) "Purpose: ${event.eventPurpose}" else ""

        return template
            .replace("{{name}}", event.customerName.ifBlank { "there" })
            .replace("{{location}}", locationName)
            .replace("{{date}}", date)
            .replace("{{time}}", timeRange)
            .replace("{{guests}}", event.guestCount.toString())
            .replace("{{cost}}", event.cost?.let { "\$$it" } ?: "")
            .replace("{{deposit}}", event.deposit?.let { "\$$it" } ?: "")
            .replace("{{status}}", event.status.name.lowercase().replaceFirstChar { it.uppercase() })
            .replace("{{notes}}", event.notes ?: "")
            .replace("{{notesBlock}}", notesBlock)
            .replace("{{purpose}}", event.eventPurpose ?: "")
            .replace("{{purposeBlock}}", purposeBlock)
            .replace("{{signature}}", signature.ifBlank { "" })
    }

    private fun formatEmailDate(epochMs: Long): String {
        if (epochMs <= 0L) return ""
        return try {
            val dt = Instant.fromEpochMilliseconds(epochMs).toLocalDateTime(TimeZone.UTC)
            val dow = dt.date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
            val month = dt.month.name.lowercase().replaceFirstChar { it.uppercase() }
            "$dow, $month ${dt.dayOfMonth}, ${dt.year}"
        } catch (e: Exception) {
            ""
        }
    }

    fun hideEmailMissing() {
        _state.update { it.copy(showEmailMissing = false) }
    }

    fun clearEmailMessage() {
        _state.update { it.copy(emailMessage = null) }
    }

    fun clearEmailSentSuccess() {
        _state.update { it.copy(emailSentSuccess = false) }
    }

    fun deleteEvent() {
        val isOwner = _state.value.currentUser?.role == UserRole.OWNER
        screenModelScope.launch {
            _state.update { it.copy(isDeleting = true, showDeleteConfirm = false) }

            try {
                if (isOwner) {
                    eventRepository.hardDeleteEvent(eventId)
                } else {
                    eventRepository.deleteEvent(eventId)
                }
                _state.update { it.copy(isDeleting = false, isDeleted = true) }
            } catch (e: Throwable) {
                _state.update { 
                    it.copy(
                        isDeleting = false, 
                        error = e.message ?: "Failed to delete event"
                    ) 
                }
            }
        }
    }

    fun updateStatus(status: EventStatus) {
        val event = _state.value.event ?: return
        
        screenModelScope.launch {
            try {
                val updatedEvent = event.copy(status = status)
                eventRepository.updateEvent(eventId, updatedEvent)
                _state.update { it.copy(event = updatedEvent) }
            } catch (e: Throwable) {
                _state.update { 
                    it.copy(error = e.message ?: "Failed to update status") 
                }
            }
        }
    }

    fun refresh() {
        loadEvent(eventId)
    }
}

private const val DEFAULT_EMAIL_TEMPLATE = """Hi {{name}},

Please find the confirmation details for your upcoming event at {{location}} below:

Location: {{location}}
Date: {{date}}
Time: {{time}}
Guest Count: {{guests}}
{{purposeBlock}}
Total Cost: {{cost}}
Deposit: {{deposit}}
Status: {{status}}

Thanks again for booking your party at {{location}}, we are here to help you host a great event. Please reply if you have any questions regarding the details above.


See you soon,


{{signature}}"""
